<template>
    <div class="mt-2">
      <!-- Navigation Tabs -->
      <ul class="nav nav-tabs">
        <li v-for="(tab, index) in tabs" :key="index" class="nav-item">
          <a
            class="nav-link"
            :class="{ active: activeTab === index }"
            href="#"
            @click.prevent="selectTab(index)"
          >
            {{ tab.label }}
          </a>
        </li>
      </ul>
  
      <!-- Tab Content -->
      <div class="tab-content mt-3">
        <component
          :is="activeTabComponent"
          :key="`${store.testData?._id || 'no-test'}-${activeTab}-${store.testData?.elements?.length || 0}`"
          v-bind="getComponentProps(activeTabComponent)"
          :show-item-bank="showItemBank"
          @toggle-item-bank="toggleItemBank"
          @update:testData="handleTestDataUpdate"
        />
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, computed, watch } from "vue";
  import { useTestStore } from "../stores/testStore";
  import ContainerEditor from "./testTabSystem/ContainerEditor.vue";
  import TestPreview from "./testTabSystem/TestPreview.vue";
  import Scoreboard from "./testTabSystem/Scoreboard.vue";
  import Psychometrics from "./testTabSystem/Psychometrics.vue";
  import CATEditor from "./CAT/CATEditor.vue";
  import CATTestPreview from "./CAT/CATTestPreview.vue";
  import ConditionalTestEditor from "./conditional/ConditionalTestEditor.vue";
  import ConditionalTestPreview from "./conditional/ConditionalTestPreview.vue";
  
  const props = defineProps({
    testId: String, // Recevoir l'ID du test depuis le parent
  });


  const showItemBank = ref(false);

function toggleItemBank() {
  showItemBank.value = !showItemBank.value;
}

  // Store pour accéder aux données du test
  const store = useTestStore();
  
  // Computed pour déterminer le type de test
  const isAdaptiveTest = computed(() => {
    return store.testData?.progression === 'adaptive';
  });

  const isConditionalTest = computed(() => {
    return store.testData?.progression === 'conditional';
  });

  // Define tabs with their labels and components
  const tabs = computed(() => {
    if (isAdaptiveTest.value) {
      return [
        { label: "CAT Editor", component: CATEditor },
        { label: "CAT Preview", component: CATTestPreview },
        { label: "Score Board", component: Scoreboard },
        { label: "Psychometrics", component: Psychometrics },
      ];
    } else if (isConditionalTest.value) {
      return [
        { label: "Conditional Editor", component: ConditionalTestEditor },
        { label: "Conditional Test Preview", component: ConditionalTestPreview },
        { label: "Score Board", component: Scoreboard },
        { label: "Psychometrics", component: Psychometrics },
      ];
    } else {
      return [
        { label: "Editor", component: ContainerEditor },
        { label: "Test Preview", component: TestPreview },
        { label: "Score Board", component: Scoreboard },
        { label: "Psychometrics", component: Psychometrics },
      ];
    }
  });
  
  // Active tab index
  const activeTab = ref(0);
  
  // Computed property to determine the active component
  const activeTabComponent = computed(() => tabs.value[activeTab.value].component);
  
  // Function to provide props based on the component
  const getComponentProps = (component) => {
    if (component === ContainerEditor) {
      return {
        prompt: "This is a fake Editor to wait", // Example prompt
      };
    } else if (component === CATEditor) {
      return {
        testData: store.testData || {}
      };
    } else if (component === ConditionalTestEditor) {
      return {
        testData: store.testData || {}
      };
    }
    return {}; // No props for other components
  };
  
  // Method to select a tab
  function selectTab(index) {
    activeTab.value = index;
  }

  // Handler for test data updates from CATEditor
  const handleTestDataUpdate = (updatedTestData) => {
    console.log('📝 Receiving test data update from CATEditor:', updatedTestData);
    store.updateTestData(updatedTestData);
  }

  // Réinitialiser l'onglet actif quand le type de test change
  watch(() => isAdaptiveTest.value, () => {
    activeTab.value = 0; // Retourner au premier onglet
  });

  watch(() => props.testId, (newTestID) => {
    console.log("Nouveau test sélectionné :", newTestID);
    activeTab.value = 0; // Réinitialiser l'onglet actif
  });
  </script>
  
  <style scoped>
  .nav-tabs .nav-link.active {
    font-weight: bold;
    border-color: #dee2e6 #dee2e6 #fff;
  }
  </style>
  